import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Alert, Badge, Button, Card, EmptyState } from '../components/ui.jsx';
import {
  buildCampaignSkipReasonRows,
  buildCandidateImportSummaryCards,
  getCandidateCampaignBadge,
  getCandidateImportActionLabel,
  getCandidateImportStatus,
  getCreatedApplicantLinks,
  getImportResultLinks,
  getPreviewImportEligibility,
} from '../utils/candidateCsvImportUi';

const SUMMARY_TONES = {
  gray: 'neutral',
  green: 'success',
  amber: 'warning',
  red: 'danger',
};

const ROW_TONE_CLASSES = {
  green: 'border-l-success-border bg-white',
  amber: 'border-l-warning-border bg-warning-bg/35',
  red: 'border-l-danger-border bg-danger-bg/50',
};

function cx(...classes) {
  return classes.flat().filter(Boolean).join(' ');
}

export default function CandidateCsvImportPanel() {
  const fileInputRef = useRef(null);
  const [filename, setFilename] = useState('');
  const [csvText, setCsvText] = useState('');
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const eligibility = getPreviewImportEligibility(preview);
  const summary = result || preview;
  const applicantLinks = getCreatedApplicantLinks(result);
  const skipReasonRows = buildCampaignSkipReasonRows(result);
  const resultLinks = getImportResultLinks(result);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setError('');
    setPreview(null);
    setResult(null);
    setFilename(file?.name || '');

    if (!file) {
      setCsvText('');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setCsvText('');
      setError('Choose a CSV export file.');
      return;
    }

    try {
      const text = await file.text();
      setCsvText(text);
    } catch {
      setCsvText('');
      setError('Could not read the selected file.');
    }
  };

  const handlePreview = async () => {
    if (!csvText.trim() || loadingPreview) return;
    setLoadingPreview(true);
    setError('');
    setResult(null);
    try {
      const data = await api.postText('/candidate-imports/preview', csvText, {
        'Content-Type': 'text/csv',
      });
      setPreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    if (!eligibility.canImport || importing) return;
    setImporting(true);
    setError('');
    try {
      const data = await api.postText('/candidate-imports', csvText, {
        'Content-Type': 'text/csv',
        ...(filename ? { 'X-Import-Filename': encodeURIComponent(filename) } : {}),
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setFilename('');
    setCsvText('');
    setPreview(null);
    setResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <Card padding="spacious">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-blue">CSV import</p>
            <h2 className="mt-1 text-xl font-bold tracking-[-0.01em] text-text-primary">Import applicants from CSV</h2>
            <p className="mt-1 max-w-2xl text-sm text-text-muted">
              Upload a candidate CSV to preview rows before adding them to the ATS. Review validation and duplicate decisions before importing.
            </p>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={resetImport}>Clear file</Button>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <label htmlFor="candidate-csv-file" className="block text-sm font-semibold text-text-primary">
              CSV export file
            </label>
            <div className="mt-2 rounded-section border-2 border-dashed border-border-strong bg-surface-subtle p-5 transition-colors hover:border-brand-blue">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-card bg-brand-blueSoft text-sm font-bold text-brand-blue ring-1 ring-info-border">CSV</span>
                  <div>
                    <p className="font-semibold text-text-primary">Drop CSV here or choose a file</p>
                    <p className="mt-1 text-sm text-text-muted">CSV only. Review the preview before importing.</p>
                    {filename ? (
                      <p className="mt-2 text-xs font-semibold text-brand-blue">Selected: {filename}</p>
                    ) : (
                      <p className="mt-2 text-xs text-text-muted">No CSV selected</p>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  id="candidate-csv-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFileChange}
                  className="input max-w-sm bg-white file:mr-3 file:rounded-md file:border-0 file:bg-brand-blueSoft file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-brand-blue hover:file:bg-info-border/40"
                />
              </div>
            </div>
          </div>
          <Button type="button" onClick={handlePreview} disabled={!csvText.trim() || loadingPreview} loading={loadingPreview} className="lg:min-w-40">
            {loadingPreview ? 'Previewing...' : 'Preview import'}
          </Button>
        </div>

        {error && <Alert variant="danger" className="mt-4" role="alert">{error}</Alert>}
      </Card>

      {!summary && !error && (
        <Card padding="compact">
          <EmptyState
            title="No CSV selected"
            description="Choose a CSV file to preview applicants before importing. Candidate contact details stay hidden from the preview summary."
            icon="⇪"
          />
        </Card>
      )}

      {summary && (
        <Card padding="spacious">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-base font-bold text-text-primary">{result ? 'Import complete' : 'Campaign enrollment preview'}</h3>
              <p className="mt-1 text-sm text-text-muted">
                {result
                  ? 'Eligible applicants were imported and campaign enrollment was processed.'
                  : 'Importing does not contact anyone until you commit. Eligible new applicants will enter the first-contact campaign after commit.'}
              </p>
              <p className="mt-1 text-xs text-text-muted">
                Candidate names, emails, and phone numbers are intentionally hidden on this screen.
              </p>
            </div>
            <Button
              type="button"
              variant={result ? 'secondary' : 'success'}
              onClick={handleImport}
              disabled={!eligibility.canImport || importing || Boolean(result)}
              loading={importing}
              title={eligibility.reason || undefined}
            >
              {result ? 'Imported' : importing ? 'Importing...' : 'Commit import and start campaigns'}
            </Button>
          </div>

          {!eligibility.canImport && !result && (
            <Alert variant="warning" className="mt-4">{eligibility.reason}</Alert>
          )}

          {result && (
            <Alert variant="success" className="mt-4">
              Import complete. Campaign enrollment was processed for eligible applicants; skipped rows are summarized below.
            </Alert>
          )}

          {!result && preview?.errorRows > 0 && (
            <Alert variant="warning" className="mt-4">
              Some rows need review. Resolve or skip conflict rows before committing.
            </Alert>
          )}

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {buildCandidateImportSummaryCards(summary).map(card => (
              <div key={card.label} className="rounded-card border border-border-default bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-[0.08em] text-text-muted">{card.label}</p>
                  <Badge variant={SUMMARY_TONES[card.tone]}>{card.tone === 'gray' ? 'total' : card.tone}</Badge>
                </div>
                <p className="mt-3 text-3xl font-bold tracking-[-0.02em] text-text-primary">{card.value}</p>
              </div>
            ))}
          </div>

          {result && (
            <div className="mt-5 flex flex-wrap gap-2">
              <Button as={Link} to={resultLinks.importedApplicantsHref} size="sm">View imported applicants</Button>
              <Button as={Link} to={resultLinks.needsAttentionHref} variant="secondary" size="sm">View needs attention</Button>
              <Button type="button" variant="ghost" size="sm" onClick={resetImport}>Import another CSV</Button>
            </div>
          )}

          {skipReasonRows.length > 0 && (
            <div className="mt-5 overflow-hidden rounded-card border border-border-default">
              <div className="border-b border-border-default bg-surface-subtle p-4">
                <h4 className="text-sm font-semibold text-text-primary">Campaign skips and attention items</h4>
                <p className="mt-1 text-xs text-text-muted">Reasons are aggregate and safe; raw row errors and provider details are not shown.</p>
              </div>
              <div className="overflow-x-auto table-scrollbar">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="bg-white text-xs font-bold uppercase tracking-[0.04em] text-text-muted">
                    <tr className="border-b border-border-default">
                      <th className="px-4 py-3">Reason</th>
                      <th className="px-4 py-3">Count</th>
                      <th className="px-4 py-3">What it means</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default text-[13px] text-text-secondary">
                    {skipReasonRows.map(row => (
                      <tr key={row.key}>
                        <td className="px-4 py-3 font-semibold text-text-primary">{row.label}</td>
                        <td className="px-4 py-3">{row.count}</td>
                        <td className="px-4 py-3">{row.meaning}</td>
                        <td className="px-4 py-3">
                          {row.href.startsWith('#') ? (
                            <a href={row.href} className="font-semibold text-brand-blue hover:underline focus-ring">{row.action}</a>
                          ) : (
                            <Link to={row.href} className="font-semibold text-brand-blue hover:underline focus-ring">{row.action}</Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {applicantLinks.length > 0 && (
            <div className="mt-5 rounded-card border border-success-border bg-success-bg p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-success-text">Created applicants</h4>
                  <p className="mt-1 text-xs text-success-text">Links use row labels only; contact details stay out of the import summary.</p>
                </div>
                <Button as={Link} to={resultLinks.importedApplicantsHref} variant="secondary" size="sm">View applicants</Button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {applicantLinks.slice(0, 12).map(link => (
                  <Link
                    key={link.applicantId}
                    to={link.href}
                    className="touch-target inline-flex items-center rounded-app border border-success-border bg-white px-3 py-2 text-sm font-semibold text-success-text hover:bg-success-bg focus-ring"
                  >
                    {link.label}
                  </Link>
                ))}
                {applicantLinks.length > 12 && (
                  <span className="inline-flex items-center px-3 py-2 text-sm text-success-text">
                    +{applicantLinks.length - 12} more
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {preview?.rows?.length > 0 && (
        <Card id="import-preview-rows" padding="compact" className="overflow-hidden p-0">
          <div className="border-b border-border-default p-4">
            <h3 className="font-bold text-text-primary">Preview rows</h3>
            <p className="mt-1 text-sm text-text-muted">Review mapped fields and fix CSV issues before importing. Contact details are not shown here.</p>
          </div>
          <div className="overflow-x-auto table-scrollbar">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-surface-subtle text-xs font-bold uppercase tracking-[0.04em] text-text-muted">
                <tr className="border-b border-border-default">
                  <th className="px-4 py-3">Row</th>
                  <th className="px-4 py-3">Action</th>
                  <th className="px-4 py-3">Status / result</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">CSV status</th>
                  <th className="px-4 py-3">Qualifications</th>
                  <th className="px-4 py-3">Fields present</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default text-[13px] text-text-secondary">
                {preview.rows.slice(0, 25).map(row => {
                  const status = getCandidateImportStatus(row);
                  const campaignBadge = getCandidateCampaignBadge(row);
                  return (
                    <tr key={row.rowNumber} className={cx('border-l-4 hover:bg-surface-hover', ROW_TONE_CLASSES[status.tone])}>
                      <td className="px-4 py-3 font-semibold text-text-primary">{row.rowNumber}</td>
                      <td className="px-4 py-3">{getCandidateImportActionLabel(row.action)}</td>
                      <td className="px-4 py-3"><Badge variant={SUMMARY_TONES[status.tone]}>{status.label}</Badge></td>
                      <td className="px-4 py-3"><Badge variant={SUMMARY_TONES[campaignBadge.tone]}>{campaignBadge.label}</Badge></td>
                      <td className="px-4 py-3">{row.externalStatus || '—'}</td>
                      <td className="px-4 py-3">{row.qualificationCount || 0}</td>
                      <td className="px-4 py-3">
                        {[row.hasName && 'name', row.hasEmail && 'email', row.hasPhone && 'phone'].filter(Boolean).join(', ') || 'none'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {preview.rows.length > 25 && (
            <p className="border-t border-border-default bg-surface-subtle p-3 text-sm text-text-muted">
              Showing first 25 of {preview.rows.length} rows. Import still processes the whole file.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
